import { useQuery } from '@apollo/client';
import { createContext, ReactNode, useContext, useMemo } from 'react';
import { GetFeatureFlagsQuery, GetFeatureFlagsQueryVariables } from '../client/generated/graphql';
import { getFeatureFlagsQuery } from '../client/queries/featureFlags';

export interface FeatureFlags {
    rituals: boolean;
    journal: boolean;
    ratings: boolean;
    rewards: boolean;
    goals: boolean;
}

const defaultFlags: FeatureFlags = {
    rituals: false,
    journal: false,
    ratings: false,
    rewards: false,
    goals: false,
};

const FeatureFlagContext = createContext<FeatureFlags>(defaultFlags);

export const FeatureFlagProvider = ({ children }: { children: ReactNode }) => {
    const { data } = useQuery<GetFeatureFlagsQuery, GetFeatureFlagsQueryVariables>(getFeatureFlagsQuery);

    const flags = useMemo<FeatureFlags>(() => ({
        rituals: data?.user?.featureFlags?.rituals ?? false,
        journal: data?.user?.featureFlags?.journal ?? false,
        ratings: data?.user?.featureFlags?.ratings ?? false,
        rewards: data?.user?.featureFlags?.rewards ?? false,
        goals: data?.user?.featureFlags?.goals ?? false,
    }), [data?.user?.featureFlags]);

    return (
        <FeatureFlagContext.Provider value={flags}>
            {children}
        </FeatureFlagContext.Provider>
    );
};

export const useFeatureFlag = (flagName: keyof FeatureFlags): boolean => {
    const flags = useContext(FeatureFlagContext);
    return flags[flagName] ?? false;
};

export const useFeatureFlags = (): FeatureFlags => {
    return useContext(FeatureFlagContext);
};

